import type { Dayjs } from 'dayjs';
import type { TFunction } from 'i18next';

import dayjs from 'dayjs';

import type { ActivityDTO } from 'src/types/dto/activity.dto';
import type { AppointmentDTO } from 'src/types/dto/appointment.dto';
import { InspectorType } from 'src/types/inspector';
import { PreferrenceType } from 'src/types/personnelPreferrence';

import type { ReschedulingReason } from './types';
import type { ShuffleInspector } from './types';
import { getCurrentNonBlockerAppointmentDates } from './get-current-non-blocker-appointment-dates';
import { getReschedulingReasonNotes } from './rescheduling-reason-labels';

export type InspectorTypeFilter = 'internal' | 'external' | null;

export interface ShuffleFilterDraft {
  isPreferred: boolean;
  inspectorType: InspectorTypeFilter;
  selectedInspectors: ShuffleInspector[];
  selectedCompetencyIds: number[];
  selectedBusinessUnitIds: number[];
  selectedDate: Dayjs | null;
  notes: string;
}

export interface ShuffleRescheduleFilters {
  isPreferred: boolean;
  inspectorType: InspectorTypeFilter;
  selectedInspectorIds: number[];
  selectedCompetencyIds: number[];
  selectedBusinessUnitIds: number[];
  selectedDates: Dayjs[];
  notes: string;
  selectedReason?: ReschedulingReason | null;
}

export const createEmptyShuffleFilterDraft = (): ShuffleFilterDraft => ({
  isPreferred: false,
  inspectorType: null,
  selectedInspectors: [],
  selectedCompetencyIds: [],
  selectedBusinessUnitIds: [],
  selectedDate: null,
  notes: '',
});

export const toShuffleRescheduleFilters = (
  draft: ShuffleFilterDraft,
  options?: {
    selectedReason?: ReschedulingReason | null;
    currentActivity?: ActivityDTO;
    appointment?: AppointmentDTO;
  }
): ShuffleRescheduleFilters => {
  let selectedDates = draft.selectedDate ? [draft.selectedDate.startOf('day')] : [];

  if (
    options?.selectedReason === 'person_cancelled' &&
    options.currentActivity
  ) {
    const fixedDates = getCurrentNonBlockerAppointmentDates(
      options.currentActivity,
      options.appointment
    );

    if (fixedDates.length > 0) {
      selectedDates = fixedDates;
    }
  }

  return {
    isPreferred: draft.isPreferred,
    inspectorType: draft.inspectorType,
    selectedInspectorIds: draft.selectedInspectors.map((inspector) => inspector.id),
    selectedCompetencyIds: draft.selectedCompetencyIds,
    selectedBusinessUnitIds: draft.selectedBusinessUnitIds,
    selectedDates,
    notes: draft.notes,
  };
};

export interface RescheduleSuggestionsRequestBody {
  preferredDates?: string[];
  preferredInspectorIds?: number[];
  competencyIds?: number[];
  businessUnitIds?: number[];
  type?: InspectorType;
  prefferenceType?: PreferrenceType;
  notes?: string;
}

export const buildRescheduleRequestBody = (
  filters: ShuffleRescheduleFilters | null,
  t?: TFunction<'shuffle'>
): RescheduleSuggestionsRequestBody => {
  const body: RescheduleSuggestionsRequestBody = {};
  if(!filters) {
    return body;
  }

  if (filters.selectedInspectorIds.length > 0) {
    body.preferredInspectorIds = filters.selectedInspectorIds;
  }

  if (filters.selectedDates.length > 0) {
    body.preferredDates = filters.selectedDates.map((date) => dayjs(date).format('YYYY-MM-DD'));
  }

  if (filters.inspectorType === 'internal') {
    body.type = InspectorType.INTERNAL;
  } else if (filters.inspectorType === 'external') {
    body.type = InspectorType.EXTERNAL;
  }

  if (filters.isPreferred) {
    body.prefferenceType = PreferrenceType.PREFERRED;
  }

  if (filters.selectedCompetencyIds.length > 0) {
    body.competencyIds = filters.selectedCompetencyIds;
  }

  if (filters.selectedBusinessUnitIds.length > 0) {
    body.businessUnitIds = filters.selectedBusinessUnitIds;
  }

  const trimmedNotes = filters.notes.trim();
  if (trimmedNotes) {
    body.notes = trimmedNotes;
  } else if (filters.selectedReason && t) {
    body.notes = getReschedulingReasonNotes(filters.selectedReason, t);
  }

  return body;
};
